/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { observable, action } from "mobx";
import { isValidPageIndex } from "../Utils/Common";
import { getNumPages } from "../selectors/commonAppSelectors";
import { canvasOperation } from "../Constants/enums";
import CanvasQuad from "./CanvasQuad";
import { transformViewToRotatedView } from "../Utils/PageUtils";

class PageHighlightsStore {
  @observable pagesQuadsArrayMap;
  @observable canvasOperation;

  constructor() {
    this.init();
  }

  initStore(stores) {
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.pagesQuadsArrayMap = new Map();
    this.canvasOperation = canvasOperation.CANVAS_OP_NONE;
  }

  @action
  setCanvasOperation(newCanvasOperation = canvasOperation.CANVAS_OP_NONE) {
    this.canvasOperation = newCanvasOperation;
  }

  // endPage is exclusive
  // If no end page provided, all pages from start to last page of PDF are cleared.
  @action
  clearQuadsForPages(start, end) {
    let endPage = end;
    const startPage = start;
    if (startPage !== undefined && isValidPageIndex(this.stores, startPage)) {
      if (endPage === undefined || !isValidPageIndex(this.stores, endPage)) {
        endPage = getNumPages(this.stores);
      }

      for (let i = startPage; i < endPage; i += 1) {
        if (this.pagesQuadsArrayMap.has(i)) this.pagesQuadsArrayMap.delete(i);
      }
    }
  }

  @action
  updatePageCanvasQuads(pageIndex, quadsArray, replaceQuads = false) {
    if (isValidPageIndex(this.stores, pageIndex) && Array.isArray(quadsArray)) {
      const newQuadsArray = quadsArray
        .filter(quadObj => CanvasQuad.areQuadPointsValid(quadObj.quadPoints))
        .map(quadObj => new CanvasQuad(quadObj));
      if (!this.pagesQuadsArrayMap.has(pageIndex)) {
        this.pagesQuadsArrayMap.set(pageIndex, []);
      }
      const updatedQuadsArray = replaceQuads
        ? newQuadsArray
        : [...this.pagesQuadsArrayMap.get(pageIndex), ...newQuadsArray];
      this.pagesQuadsArrayMap.set(pageIndex, updatedQuadsArray);
    }
  }

  @action
  rotateHighlights(angle) {
    [...this.pagesQuadsArrayMap].forEach(([pageIndex, pageQuads]) => {
      const rotateQuadFn = (x, y) =>
        transformViewToRotatedView(x, y, +pageIndex, this.stores, 1, angle);
      const rotatedPageQuads = pageQuads
        .map(quad => quad.transform(rotateQuadFn))
        .slice();
      this.updatePageCanvasQuads(+pageIndex, rotatedPageQuads, true);
    });
  }
}

export default PageHighlightsStore;
